import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Sparkles, Loader2, ArrowRight, X, MessageSquare,
  TrendingUp, TrendingDown, RefreshCw, Download, FileDown, Mic, Plus,
  ThumbsUp, ThumbsDown, History,
} from 'lucide-react';
import { useNLQuery } from '../../../hooks/useAnalytics';
import { analyticsService } from '../../../services/analytics.service';
import type { NLQueryResponse, NLQueryMetric, NLQueryChart, NLQueryDrillDown } from '../../../services/analytics.service';
import { BarChart, LineChart, AreaChart, PieChart, DonutChart } from '../../../components/charts';
import { useAuthStore } from '../../../stores/authStore';

const EXAMPLES_BY_ROLE: Record<string, string[]> = {
  admin: [
    'What is the main reason of ticket generation?',
    'How many open tickets?',
    'Tickets by priority this week',
    'Compare this week vs last week',
    'Who is my top agent?',
  ],
  owner: [
    'What is the main reason of ticket generation?',
    'How many open tickets?',
    'Tickets by priority this week',
    'Compare this week vs last week',
    'Who is my top agent?',
  ],
  supervisor: [
    "My team's SLA compliance this week",
    "Top agent on my team",
    'Unassigned tickets today',
    'Tickets by channel this week',
    'Forecast next week tickets',
  ],
  agent: [
    'How many tickets assigned to me?',
    'My SLA compliance this week',
    'How am I performing',
    'My response time this week',
    'Are there overdue tickets?',
  ],
};

// Up to 2 common supported locales that the SpeechRecognition API handles well.
// Anything else falls back to en-US so the router (English-only intent
// matching augmented by locale dict) can understand the transcript.
const SUPPORTED_VOICE_LANGS = new Set(['en', 'es', 'fr', 'hi', 'ar']);

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

function voiceLangFor(navLang: string): string {
  const primary = (navLang || 'en').split('-')[0].toLowerCase();
  if (SUPPORTED_VOICE_LANGS.has(primary)) return navLang || 'en-US';
  return 'en-US';
}

/** Coerce an error payload into a displayable string. FastAPI returns
 *  `detail` as an object/array for validation errors, which would otherwise
 *  stringify to "[object Object]". Returns undefined when no text can be
 *  extracted so callers can fall through to the next candidate. */
export function toErrorText(v: unknown): string | undefined {
  if (typeof v === 'string') return v.trim() || undefined;
  if (Array.isArray(v)) {
    const msgs = v
      .map((e) => (e && typeof e === 'object' && 'msg' in e ? (e as any).msg : e))
      .filter((m): m is string => typeof m === 'string' && m.length > 0);
    return msgs.length ? msgs.join('; ') : undefined;
  }
  if (v && typeof v === 'object') {
    const m = (v as any).msg ?? (v as any).message;
    return typeof m === 'string' && m.length > 0 ? m : undefined;
  }
  return undefined;
}

function formatUpdatedAgo(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

/**
 * Date.prototype.toISOString() throws RangeError: Invalid time value on an
 * Invalid Date (unlike toLocaleString(), which just renders "Invalid Date").
 * History entries come from sessionStorage with no schema validation, so a
 * corrupted/stale-schema `at` value crashed this every time the history
 * dropdown opened (Bug #9408). The app itself always writes a valid
 * `at: Date.now()`, so this only guards against tampered/legacy storage.
 */
export function formatHistoryTimestamp(at: number): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? '' : formatUpdatedAgo(d.toISOString());
}

function toCsv(rows: Array<Record<string, any>>): string {
  if (!rows.length) return '';
  const keySet = new Set<string>();
  for (const r of rows) {
    for (const k of Object.keys(r)) keySet.add(k);
  }
  const keys = Array.from(keySet);
  const esc = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = keys.join(',');
  const body = rows.map((r) => keys.map((k) => esc(r[k])).join(',')).join('\n');
  return `${header}\n${body}`;
}

function downloadBlob(data: string | Blob, filename: string, mime: string) {
  const blob = typeof data === 'string' ? new Blob([data], { type: mime }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Minimal printable-HTML→PDF via the browser's print dialog.
 *  Zero dependencies; user picks "Save as PDF" in the dialog.
 */
function printNode(title: string, rootHtml: string) {
  const w = window.open('', '_blank', 'width=800,height=700');
  if (!w) return;
  w.document.write(`<!doctype html>
<html><head><title>${title}</title>
<style>
  body { font: 14px/1.4 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; padding: 24px; color:#111; }
  h1 { font-size: 18px; margin: 0 0 8px; }
  .meta { color:#666; font-size:12px; margin-bottom: 16px; }
  .pill { display:inline-block; background:#f3f4f6; padding:4px 10px; margin:4px 6px 0 0; border-radius:8px; }
  table { border-collapse: collapse; margin-top: 12px; font-size: 13px; }
  th, td { border: 1px solid #e5e7eb; padding: 6px 10px; text-align: left; }
</style>
</head><body>${rootHtml}</body></html>`);
  w.document.close();
  w.focus();
  // Small delay lets the print dialog render after styles apply
  setTimeout(() => { try { w.print(); } catch {} }, 250);
}

function drillDownToSearchParams(drill: NLQueryDrillDown | undefined): string {
  if (!drill || !drill.filters) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(drill.filters)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      v.forEach((item) => sp.append(k, String(item)));
    } else {
      sp.append(k, String(v));
    }
  }
  return sp.toString();
}

const SESSION_STORAGE_KEY = 'centraoneplus:nlQueryBar:sessionId';
const HISTORY_STORAGE_KEY = 'centraoneplus:nlQueryBar:history';

interface HistoryEntry {
  at: number;
  query: string;
  intent?: string;
  answer?: string;
}

function loadOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
  } catch {}
  const fresh = globalThis.crypto && 'randomUUID' in globalThis.crypto
    ? globalThis.crypto.randomUUID()
    : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try { sessionStorage.setItem(SESSION_STORAGE_KEY, fresh); } catch {}
  return fresh;
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(HISTORY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]) {
  try {
    sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(entries.slice(-10)));
  } catch {}
}

interface NLQueryBarProps {
  onNavigateToReport?: (reportType: string, filters?: Record<string, any>) => void;
  className?: string;
  /**
   * 'machine' dresses the bar in the machine palette (phase 38 · 23b), marking
   * it as the one control on the page that answers rather than filters. Reports
   * keeps the default so this change does not travel to a screen 23b never
   * looked at.
   */
  variant?: 'default' | 'machine';
  /**
   * Sits inside another surface (e.g. the Reports header tab): the bar drops
   * its own card border/shadow, matches the 34px header controls, and its
   * dropdowns open wider than the bar, anchored to its right edge.
   */
  embedded?: boolean;
  /** Edge an embedded bar's dropdowns line up with (default 'right'). */
  dropAlign?: 'left' | 'right';
}

export function NLQueryBar({
  onNavigateToReport,
  className = '',
  variant = 'default',
  embedded = false,
  dropAlign = 'right',
}: NLQueryBarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const role = (user?.role || 'agent').toLowerCase();

  const examples = useMemo(() => {
    return EXAMPLES_BY_ROLE[role] || EXAMPLES_BY_ROLE.agent;
  }, [role]);

  const [query, setQuery] = useState('');
  const [result, setResult] = useState<NLQueryResponse | null>(null);
  const [showExamples, setShowExamples] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [listening, setListening] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>(() => loadHistory());
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resultPanelRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const sessionIdRef = useRef<string>(loadOrCreateSessionId());

  const nlQuery = useNLQuery();

  // Track dark-mode toggles so chart palette refreshes when user flips theme
  useEffect(() => {
    const detect = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    detect();
    const observer = new MutationObserver(detect);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const recordHistory = useCallback((entry: HistoryEntry) => {
    setHistory((prev) => {
      const next = [...prev.filter((h) => h.query !== entry.query), entry].slice(-10);
      saveHistory(next);
      return next;
    });
  }, []);

  const runQuery = useCallback(
    async (q: string, refresh = false) => {
      const trimmed = q.trim();
      if (!trimmed) return;
      setShowExamples(false);
      setShowHistory(false);
      setFeedback(null);
      try {
        const response = await nlQuery.mutateAsync({
          query: trimmed,
          sessionId: sessionIdRef.current,
          refresh,
        });
        setResult(response);
        recordHistory({
          at: Date.now(),
          query: trimmed,
          intent: response.intent?.type,
          answer: response.answer,
        });
      } catch {
        // Error handled via mutation state
      }
    },
    [nlQuery, recordHistory],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      runQuery(query);
    }
    if (e.key === 'Escape') {
      setResult(null);
      setShowExamples(false);
      setShowHistory(false);
    }
  };

  const handleExampleClick = (example: string) => {
    setQuery(example);
    setShowExamples(false);
    runQuery(example);
  };

  const handleClear = () => {
    setQuery('');
    setResult(null);
    inputRef.current?.focus();
  };

  const handleNewSession = () => {
    const fresh = globalThis.crypto && 'randomUUID' in globalThis.crypto
      ? globalThis.crypto.randomUUID()
      : `sess-${Date.now()}`;
    sessionIdRef.current = fresh;
    try { sessionStorage.setItem(SESSION_STORAGE_KEY, fresh); } catch {}
    setResult(null);
    setQuery('');
    inputRef.current?.focus();
  };

  const handleDrillDown = useCallback(
    (drill: NLQueryDrillDown | undefined) => {
      if (!drill) return;
      const qs = drillDownToSearchParams(drill);
      if (!qs) return;
      setResult(null);
      navigate(`/tickets?${qs}`);
    },
    [navigate],
  );

  const handleExportCsv = () => {
    if (!result) return;
    const rows: Array<Record<string, any>> = [];
    if (result.chart?.data?.length) {
      rows.push(...result.chart.data.map((d) => ({ ...d })));
    } else if (result.metrics?.length) {
      rows.push(...result.metrics.map((m) => ({ label: m.label, value: m.value })));
    }
    if (!rows.length) return;
    const csv = `# ${result.query}\n# ${result.answer}\n${toCsv(rows)}`;
    downloadBlob(csv, `nl-query-${Date.now()}.csv`, 'text/csv;charset=utf-8');
  };

  const handleExportPdf = () => {
    if (!result) return;
    const pills = (result.metrics || [])
      .map((m) => `<div class="pill"><b>${m.label}:</b> ${m.value}${m.change ? ` (${m.change.direction === 'up' ? '↑' : '↓'}${m.change.value}%)` : ''}</div>`)
      .join(' ');
    const rows = (result.chart?.data || [])
      .map((d) => {
        const xKey = result.chart?.config?.xKey || 'name';
        const yKey = result.chart?.config?.yKeys?.[0] || 'value';
        return `<tr><td>${d[xKey] ?? ''}</td><td>${d[yKey] ?? ''}</td></tr>`;
      })
      .join('');
    const table = rows
      ? `<table><thead><tr><th>Label</th><th>Value</th></tr></thead><tbody>${rows}</tbody></table>`
      : '';
    const html = `
      <h1>${result.query}</h1>
      <div class="meta">Generated ${new Date(result.generatedAt).toLocaleString()}${result.intent?.type ? ` · ${result.intent.type}` : ''}</div>
      <p>${result.answer}</p>
      <div>${pills}</div>
      ${result.chart?.title ? `<h2 style="margin-top:16px;font-size:14px;">${result.chart.title}</h2>` : ''}
      ${table}
    `;
    printNode('NL Query Result', html);
  };

  const handleToggleVoice = () => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new Ctor();
    rec.lang = voiceLangFor(navigator.language || 'en-US');
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e: any) => {
      const transcript = e?.results?.[0]?.[0]?.transcript;
      if (transcript) {
        setQuery(transcript);
        runQuery(transcript);
      }
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    try {
      rec.start();
    } catch {
      setListening(false);
    }
  };

  const sendFeedback = useCallback(
    async (rating: 'up' | 'down') => {
      if (!result) return;
      setFeedback(rating);
      try {
        await analyticsService.nlQueryFeedback({
          query: result.query,
          intent: result.intent?.type,
          rating,
        });
      } catch {
        // Non-blocking
      }
    },
    [result],
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowExamples(false);
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const voiceAvailable = typeof window !== 'undefined' && !!getSpeechRecognitionCtor();
  const errorMessage = toErrorText((nlQuery.error as any)?.response?.data?.detail)
    ?? toErrorText((nlQuery.error as any)?.response?.data?.message)
    ?? toErrorText((nlQuery.error as any)?.message);
  const errorStatus = (nlQuery.error as any)?.response?.status as number | undefined;
  const isRateLimited = errorStatus === 429;
  const isClientError = errorStatus === 400;

  const metricCount = result?.metrics?.length ?? 0;
  const isMachine = variant === 'machine';
  const dropPos = embedded
    ? `${dropAlign === 'left' ? 'left-0' : 'right-0'} w-[min(44rem,calc(100vw-3rem))]`
    : 'left-0 right-0';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        className={
          isMachine && embedded
            ? 'flex h-[34px] items-center gap-2 rounded-[10px] border border-[var(--co-machine-line)] bg-[var(--co-machine-wash)] pl-2 pr-1 transition-all focus-within:border-[var(--co-machine)]'
            : isMachine
            ? 'flex items-center gap-3 rounded-[10px] border border-[var(--co-machine-line)] bg-[var(--co-machine-wash)] px-4 py-2.5 transition-all focus-within:border-[var(--co-machine)]'
            : 'flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all'
        }
        style={isMachine && !embedded ? { boxShadow: 'var(--co-shadow-card)' } : undefined}
      >
        {isMachine ? (
          <span
            className="co-mono flex h-6 w-6 flex-none items-center justify-center rounded-[7px] border border-[var(--co-machine-line)] bg-[var(--co-raised)] text-[9px] font-bold text-[var(--co-machine-ink)]"
            aria-hidden
          >
            AI
          </span>
        ) : (
          <Sparkles className="h-4 w-4 text-primary-500 flex-shrink-0" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => !result && setShowExamples(true)}
          placeholder="Ask anything about your analytics..."
          className={
            isMachine
              ? 'flex-1 min-w-0 bg-transparent font-sans text-[14px] text-[var(--co-ink)] placeholder-[var(--co-ink-3)] outline-none'
              : 'flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none'
          }
          maxLength={500}
        />
        {query && (
          <button onClick={handleClear} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500" aria-label="Clear">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        {history.length > 0 && !result && (
          <button
            onClick={() => { setShowHistory((v) => !v); setShowExamples(false); }}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
            title="History"
            aria-label="Query history"
          >
            <History className="h-3.5 w-3.5" />
          </button>
        )}
        {voiceAvailable && (
          <button
            onClick={handleToggleVoice}
            className={`p-1.5 rounded-md ${listening ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300 animate-pulse' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
            aria-label={listening ? 'Stop listening' : 'Start voice input'}
            title={listening ? 'Listening...' : 'Voice input'}
          >
            <Mic className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          onClick={() => runQuery(query)}
          disabled={!query.trim() || nlQuery.isPending}
          className={
            isMachine && embedded
              ? 'flex h-[26px] flex-none items-center gap-1.5 rounded-lg bg-[var(--co-machine-ink)] px-[11px] text-[12px] font-bold text-[var(--co-ink-invert)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
              : isMachine
              ? 'flex h-[34px] flex-none items-center gap-1.5 rounded-lg bg-[var(--co-machine-ink)] px-[14px] text-[12.5px] font-bold text-[var(--co-ink-invert)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
              : 'flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          }
        >
          {nlQuery.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Ask
        </button>
      </div>

      {showExamples && !result && !showHistory && (
        <div className={`absolute z-20 top-full ${dropPos} mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2`}>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 px-3 py-1.5">
            Try asking ({role}):
          </p>
          {examples.map((example) => (
            <button
              key={example}
              onClick={() => handleExampleClick(example)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors text-left"
            >
              <MessageSquare className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 dark:text-gray-500" />
              {example}
            </button>
          ))}
        </div>
      )}

      {showHistory && !result && (
        <div className={`absolute z-20 top-full ${dropPos} mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 max-h-[400px] overflow-y-auto`}>
          <div className="flex items-center justify-between px-3 py-1.5">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Recent queries</p>
            <button
              className="text-[10px] text-gray-400 hover:text-red-500 dark:text-gray-500"
              onClick={() => { setHistory([]); saveHistory([]); setShowHistory(false); }}
            >
              Clear
            </button>
          </div>
          {[...history].reverse().map((h, i) => (
            <button
              key={`${h.at}-${i}`}
              onClick={() => handleExampleClick(h.query)}
              className="w-full flex items-start gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors text-left"
            >
              <MessageSquare className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5 dark:text-gray-500" />
              <div className="flex-1 min-w-0">
                <div className="truncate">{h.query}</div>
                {h.answer && <div className="text-[11px] text-gray-400 truncate dark:text-gray-500">{h.answer}</div>}
              </div>
              <span className="text-[10px] text-gray-400 whitespace-nowrap dark:text-gray-500">{formatHistoryTimestamp(h.at)}</span>
            </button>
          ))}
        </div>
      )}

      {result && (
        <div
          ref={resultPanelRef}
          className={`absolute z-20 top-full ${dropPos} mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 space-y-3 overflow-y-auto`}
          style={{ maxHeight: 'min(520px, 60vh)' }}
        >
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary-500" />
                <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide">AI Answer</span>
                {result.cacheInfo?.hit && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    cached · {formatUpdatedAgo(result.generatedAt)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => runQuery(result.query, true)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
                  title="Refresh (bypass cache)"
                  aria-label="Refresh"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={handleExportCsv}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
                  title="Export CSV"
                  aria-label="Export CSV"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={handleExportPdf}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
                  title="Export PDF"
                  aria-label="Export PDF"
                >
                  <FileDown className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={handleNewSession}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
                  title="New question (clear session)"
                  aria-label="New question"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{result.answer}</p>
          </div>

          {result.metrics && result.metrics.length > 0 && (
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(metricCount, 1), 3)}, minmax(0, 1fr))` }}
            >
              {result.metrics.map((m, idx) => (
                <MetricPill key={idx} metric={m} onDrillDown={handleDrillDown} />
              ))}
            </div>
          )}

          {result.chart && result.chart.data && result.chart.data.length > 0 && (
            <div className="pt-1">
              {result.chart.title && (
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{result.chart.title}</p>
              )}
              <div className="h-56 w-full">
                <InlineChart chart={result.chart} onDrillDown={handleDrillDown} isDarkMode={isDarkMode} />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              {result.intent?.type?.replace(/_/g, ' ')}
            </span>
            {typeof result.intent?.confidence === 'number' && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {Math.round((result.intent.confidence ?? 0) * 100)}% confidence
              </span>
            )}
            {result.suggestedReport && (
              <button
                onClick={() => {
                  onNavigateToReport?.(result.suggestedReport!, result.suggestedFilters);
                  setResult(null);
                }}
                className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
              >
                View {result.suggestedReport} report
                <ArrowRight className="h-3 w-3" />
              </button>
            )}
            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={() => sendFeedback('up')}
                className={`p-1 rounded ${feedback === 'up' ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600' : 'text-gray-400 hover:text-emerald-600'} dark:text-gray-500`}
                title="This answer was helpful"
                aria-label="Thumbs up"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => sendFeedback('down')}
                className={`p-1 rounded ${feedback === 'down' ? 'bg-red-100 dark:bg-red-900/40 text-red-600' : 'text-gray-400 hover:text-red-600'} dark:text-gray-500`}
                title="This answer was not helpful"
                aria-label="Thumbs down"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setResult(null)}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ml-2 dark:text-gray-500"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {nlQuery.isError && !result && query.trim() && !showExamples && !showHistory && (
        <div className={`absolute z-20 top-full ${dropPos} mt-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3`}>
          <p className="text-sm text-red-700 dark:text-red-300">
            {isRateLimited
              ? 'You are sending queries too quickly. Please wait a minute.'
              : isClientError
                ? errorMessage || 'Invalid request.'
                : (errorMessage
                  ? `Query failed: ${errorMessage}`
                  : 'Failed to process query. The AI analytics service may be unavailable.')}
          </p>
        </div>
      )}
    </div>
  );
}

function MetricPill({
  metric,
  onDrillDown,
}: {
  metric: NLQueryMetric;
  onDrillDown: (d: NLQueryDrillDown | undefined) => void;
}) {
  const changeColor = metric.change?.direction === 'up'
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-400';
  const ChangeIcon = metric.change?.direction === 'up' ? TrendingUp : TrendingDown;
  const clickable = !!metric.drillDown;
  return (
    <button
      type="button"
      onClick={() => clickable && onDrillDown(metric.drillDown)}
      disabled={!clickable}
      className={`text-left rounded-lg bg-gray-50 dark:bg-gray-700/50 px-3 py-2 ${clickable ? 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer' : 'cursor-default'}`}
    >
      <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">{metric.label}</p>
      <p className="text-base font-semibold text-gray-900 dark:text-white truncate">{metric.value}</p>
      {metric.change && (
        <p className={`text-[11px] flex items-center gap-0.5 ${changeColor}`}>
          <ChangeIcon className="h-3 w-3" />
          {metric.change.value}%
        </p>
      )}
    </button>
  );
}

// Dark-mode-aware palette
const LIGHT_PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const DARK_PALETTE = ['#60a5fa', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#f472b6'];

function pickPalette(custom: string[] | undefined, isDark: boolean): string[] {
  if (custom && custom.length) return custom;
  return isDark ? DARK_PALETTE : LIGHT_PALETTE;
}

function InlineChart({
  chart,
  onDrillDown,
  isDarkMode,
}: {
  chart: NLQueryChart;
  onDrillDown: (d: NLQueryDrillDown | undefined) => void;
  isDarkMode: boolean;
}) {
  const yKey = chart.config?.yKeys?.[0] || 'value';
  const xKey = chart.config?.xKey || 'name';
  const colors = pickPalette(chart.config?.colors, isDarkMode);

  const handleRowClick = (row: any) => {
    const drill = row?.drillDown as NLQueryDrillDown | undefined;
    if (drill) onDrillDown(drill);
  };

  switch (chart.type) {
    case 'bar':
      return (
        <BarChart
          data={chart.data as any}
          bars={[{ dataKey: yKey, color: colors[0] }]}
          xAxisDataKey={xKey}
          showLegend={false}
          colorByValue={!!chart.config?.colors && chart.config.colors.length > 1}
          colors={colors}
          height={220}
          onBarClick={handleRowClick}
        />
      );
    case 'line':
      return (
        <LineChart
          data={chart.data as any}
          lines={[{ dataKey: yKey, color: colors[0] }]}
          xAxisDataKey={xKey}
          showLegend={false}
          height={220}
        />
      );
    case 'area':
      return (
        <AreaChart
          data={chart.data as any}
          areas={[{ dataKey: yKey, color: colors[0] }]}
          xAxisDataKey={xKey}
          showLegend={false}
          height={220}
        />
      );
    case 'donut':
      return (
        <DonutChart
          data={chart.data as any}
          showLegend
          colors={colors}
          height={220}
          onSliceClick={handleRowClick}
        />
      );
    case 'pie':
      return (
        <PieChart
          data={chart.data as any}
          showLegend
          colors={colors}
          height={220}
          onSliceClick={handleRowClick}
        />
      );
    default:
      return null;
  }
}
